import { useState, useCallback } from "react";
import { useGraphQLFetch } from "./use-graphql-fetch";
import { toast } from "sonner";

export interface DashboardSummary {
  totalStockItems: number;
  activeStockItems: number;
  inactiveStockItems: number;
  totalStockValue: number;
  itemsBelowMinimum: number;
  itemsAboveMaximum: number;
  itemsAtReorderPoint: number;
  itemsOutOfStock: number;
  totalWarehouses: number;
  totalLocations: number;
  totalMovementsToday: number;
  totalMovementsThisWeek: number;
  totalMovementsThisMonth: number;
  pendingStockTakes: number;
  overdueStockTakes: number;
  expiringItemsNext30Days: number;
  expiredItems: number;
  recalledItems: number;
  quarantinedItems: number;
  totalReceiptsToday: number;
  totalDispatchesToday: number;
  lastUpdated: string;
}

export interface WarehouseData {
  warehouseID: number;
  warehouseCode: string;
  warehouseName: string;
  address: string | null;
  warehouseType: string;
  totalCapacity: number | null;
  currentUtilization: number | null;
  defaultReceiveLocation: string | null;
  defaultPickLocation: string | null;
  active: boolean;
  totalLocations: number;
  activeLocations: number;
  totalCurrentStock: number;
}

export interface LocationData {
  locationID: number;
  locationCode: string;
  warehouseCode: string;
  warehouseName: string;
  aisle: string | null;
  rack: string | null;
  shelf: string | null;
  bin: string | null;
  locationType: string;
  capacity: number | null;
  currentStock: number;
  utilizationPercent: number;
  pickingSequence: number | null;
  active: boolean;
}

export interface StockTakeData {
  stockTakeID: number;
  stockTakeReference: string;
  description: string;
  stockTakeType: string;
  warehouse: string;
  warehouseCode: string;
  status: string;
  createdBy: string;
  assignedTo: string | null;
  createdAt: string;
  startedAt: string | null;
  completedAt: string | null;
  totalItems: number;
  countedItems: number;
  varianceCount: number;
  progressPercent: number;
  notes: string | null;
}

export interface StockMovementData {
  movementID: number;
  movementReference: string;
  movementDate: string;
  movementType: string;
  movementSource: string;
  stockCode: string;
  stockDescription: string;
  locationCode: string | null;
  quantity: number;
  unitPrice: number;
  lineTotal: number;
  userName: string;
  notes: string | null;
}

export interface StockItemData {
  stockItemID: number;
  stockCode: string;
  description: string;
  category: string | null;
  warehouseCode: string | null;
  locationCode: string | null;
  physicalQuantity: number;
  allocatedQuantity: number;
  freeQuantity: number;
  quantityDueIn: number;
  minQuantity: number;
  maxQuantity: number;
  reorderPoint: number;
  reorderQuantity: number;
  unitPrice: number;
  lastCostPrice: number;
  averageCost: number;
  totalValue: number;
  aBCClassification: string | null;
  stockTurnRate: number | null;
  inactive: boolean;
  lastMovementDate: string | null;
}

interface DashboardSummaryResponse {
  data?: {
    dashboard?: {
      dashboardSummary?: DashboardSummary;
    };
  };
  errors?: Array<{ message: string }>;
}

interface WarehouseListResponse {
  data?: {
    warehouses?: {
      warehouseList?: {
        items: WarehouseData[];
      };
    };
  };
  errors?: Array<{ message: string }>;
}

interface LocationListResponse {
  data?: {
    locations?: {
      locationList?: {
        items: LocationData[];
      };
    };
  };
  errors?: Array<{ message: string }>;
}

interface StockTakeListResponse {
  data?: {
    stockTakes?: {
      stockTakeList?: {
        items: StockTakeData[];
      };
    };
  };
  errors?: Array<{ message: string }>;
}

interface StockMovementListResponse {
  data?: {
    stockMovements?: {
      stockMovementList?: {
        items: StockMovementData[];
      };
    };
  };
  errors?: Array<{ message: string }>;
}

interface StockItemListResponse {
  data?: {
    stockItems?: {
      stockItemList?: {
        items: StockItemData[];
      };
    };
  };
  errors?: Array<{ message: string }>;
}

const DASHBOARD_SUMMARY_QUERY = `
  query GetDashboardSummary($warehouseCode: String) {
    dashboard {
      dashboardSummary(warehouseCode: $warehouseCode) {
        totalStockItems
        activeStockItems
        inactiveStockItems
        totalStockValue
        itemsBelowMinimum
        itemsAboveMaximum
        itemsAtReorderPoint
        itemsOutOfStock
        totalWarehouses
        totalLocations
        totalMovementsToday
        totalMovementsThisWeek
        totalMovementsThisMonth
        pendingStockTakes
        overdueStockTakes
        expiringItemsNext30Days
        expiredItems
        recalledItems
        quarantinedItems
        totalReceiptsToday
        totalDispatchesToday
        lastUpdated
      }
    }
  }
`;

const WAREHOUSE_LIST_QUERY = `
  query GetWarehouses($first: Int) {
    warehouses {
      warehouseList(first: $first) {
        items {
          warehouseID
          warehouseCode
          warehouseName
          address
          warehouseType
          totalCapacity
          currentUtilization
          defaultReceiveLocation
          defaultPickLocation
          active
          totalLocations
          activeLocations
          totalCurrentStock
        }
      }
    }
  }
`;

const LOCATION_LIST_QUERY = `
  query GetLocations($first: Int) {
    locations {
      locationList(first: $first) {
        items {
          locationID
          locationCode
          warehouseCode
          warehouseName
          aisle
          rack
          shelf
          bin
          locationType
          capacity
          currentStock
          utilizationPercent
          pickingSequence
          active
        }
      }
    }
  }
`;

const STOCK_TAKE_LIST_QUERY = `
  query GetStockTakes($first: Int) {
    stockTakes {
      stockTakeList(first: $first) {
        items {
          stockTakeID
          stockTakeReference
          description
          stockTakeType
          warehouse
          warehouseCode
          status
          createdBy
          assignedTo
          createdAt
          startedAt
          completedAt
          totalItems
          countedItems
          varianceCount
          progressPercent
          notes
        }
      }
    }
  }
`;

const STOCK_MOVEMENT_LIST_QUERY = `
  query GetStockMovements($first: Int) {
    stockMovements {
      stockMovementList(first: $first) {
        items {
          movementID
          movementReference
          movementDate
          movementType
          movementSource
          stockCode
          stockDescription
          locationCode
          quantity
          unitPrice
          lineTotal
          userName
          notes
        }
      }
    }
  }
`;

const STOCK_ITEMS_FOR_ALERTS_QUERY = `
  query GetStockItemsForAlerts($first: Int) {
    stockItems {
      stockItemList(first: $first) {
        items {
          stockItemID
          stockCode
          description
          category
          warehouseCode
          locationCode
          physicalQuantity
          allocatedQuantity
          freeQuantity
          quantityDueIn
          minQuantity
          maxQuantity
          reorderPoint
          reorderQuantity
          unitPrice
          lastCostPrice
          averageCost
          totalValue
          aBCClassification
          stockTurnRate
          inactive
          lastMovementDate
        }
      }
    }
  }
`;

export interface DashboardData {
  summary: DashboardSummary | null;
  warehouses: WarehouseData[];
  locations: LocationData[];
  stockTakes: StockTakeData[];
  recentMovements: StockMovementData[];
  stockItems: StockItemData[];
}

export const useDashboardData = () => {
  const { fetchGraphQL } = useGraphQLFetch();
  const [data, setData] = useState<DashboardData>({
    summary: null,
    warehouses: [],
    locations: [],
    stockTakes: [],
    recentMovements: [],
    stockItems: [],
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchDashboardData = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const [summaryResult, warehousesResult, locationsResult, stockTakesResult, movementsResult, stockItemsResult] =
        await Promise.all([
          fetchGraphQL<DashboardSummaryResponse["data"]>(DASHBOARD_SUMMARY_QUERY, {}),
          fetchGraphQL<WarehouseListResponse["data"]>(WAREHOUSE_LIST_QUERY, { first: 100 }),
          fetchGraphQL<LocationListResponse["data"]>(LOCATION_LIST_QUERY, { first: 200 }),
          fetchGraphQL<StockTakeListResponse["data"]>(STOCK_TAKE_LIST_QUERY, { first: 50 }),
          fetchGraphQL<StockMovementListResponse["data"]>(STOCK_MOVEMENT_LIST_QUERY, { first: 20 }),
          fetchGraphQL<StockItemListResponse["data"]>(STOCK_ITEMS_FOR_ALERTS_QUERY, { first: 1000 }),
        ]);

      if (summaryResult.errors?.length) {
        console.warn("Dashboard summary query errors:", summaryResult.errors);
      }

      setData({
        summary: summaryResult.data?.dashboard?.dashboardSummary || null,
        warehouses: warehousesResult.data?.warehouses?.warehouseList?.items || [],
        locations: locationsResult.data?.locations?.locationList?.items || [],
        stockTakes: stockTakesResult.data?.stockTakes?.stockTakeList?.items || [],
        recentMovements: movementsResult.data?.stockMovements?.stockMovementList?.items || [],
        stockItems: stockItemsResult.data?.stockItems?.stockItemList?.items || [],
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to fetch dashboard data";
      setError(message);
      console.error("Dashboard data fetch error:", err);
      toast.error(`Failed to load dashboard data: ${message}`);
    } finally {
      setIsLoading(false);
    }
  }, [fetchGraphQL]);

  return {
    data,
    isLoading,
    error,
    fetchDashboardData,
  };
};
